import React, { useState } from 'react';
import { Table, Cpu, CheckCircle, FileText } from 'lucide-react';
import { AVAILABLE_MODELS } from '../constants';
import { extractTable } from '../api/client';

export default function TableExtractionPage() {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [tableResult, setTableResult] = useState(null);
    const [selectedModel, setSelectedModel] = useState("gemini-3-flash-preview");
    const [copied, setCopied] = useState(false);

    const handleFileChange = (event) => {
        const selectedFile = event.target.files?.[0] || null;
        setFile(selectedFile);
        setError("");
        setTableResult(null);
    };

    const handleExtractTable = async () => {
        if (!file) {
            setError("Veuillez sélectionner un fichier avant de lancer l'extraction.");
            return;
        }

        setLoading(true);
        setError("");
        setTableResult(null);

        try {
            const selectedModelConfig = AVAILABLE_MODELS.find(model => model.value === selectedModel);
            const result = await extractTable(file, selectedModelConfig.provider, selectedModelConfig.value);
            setTableResult(result);
        } catch (err) {
            console.error("Table extraction error:", err);
            // More friendly error if endpoint 404s
            if (err.message && err.message.includes("404")) {
                setError("L'extraction de tableaux n'est pas encore activée sur le serveur.");
            } else {
                setError(err.message || "Une erreur est survenue lors de l'extraction.");
            }
        } finally {
            setLoading(false);
        }
    };

    const formatTableData = (tables) => {
        if (!tables || tables.length === 0) return "Aucun tableau détecté";

        return tables.map((table, tableIndex) => (
            <div key={tableIndex} className="mb-8">
                <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-bold text-gray-800">
                        Tableau {tableIndex + 1} {table.title ? `- ${table.title}` : ''}
                    </h3>
                    <span className="text-sm text-gray-500">
                        {table.rows?.length || 0} lignes × {table.columns?.length || 0} colonnes
                    </span>
                </div>

                {table.rows && table.rows.length > 0 ? (
                    <div className="overflow-x-auto border border-gray-300 rounded-lg">
                        <table className="min-w-full bg-white">
                            <thead className="bg-gray-100">
                                <tr>
                                    {table.columns?.map((col, colIndex) => (
                                        <th
                                            key={colIndex}
                                            className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-300"
                                        >
                                            {col || `Colonne ${colIndex + 1}`}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {table.rows.map((row, rowIndex) => (
                                    <tr
                                        key={rowIndex}
                                        className={rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50"}
                                    >
                                        {row.map((cell, cellIndex) => (
                                            <td
                                                key={cellIndex}
                                                className="px-4 py-3 text-sm text-gray-800 border-b border-gray-200"
                                            >
                                                {cell || ''}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800">
                        Tableau détecté mais structure non reconnue
                    </div>
                )}

                {table.raw_text && (
                    <div className="mt-4 p-3 bg-gray-100 rounded text-sm text-gray-700">
                        <span className="font-medium">Texte brut :</span> {table.raw_text}
                    </div>
                )}
            </div>
        ));
    };

    return (
        <div className="p-6 bg-gray-50 min-h-[calc(100vh-64px)]">
            <div className="max-w-6xl mx-auto">
                <h1 className="text-3xl font-bold mb-8 text-gray-800 flex items-center gap-3">
                    <Table className="w-7 h-7 text-blue-600" />
                    Extraction de Tableaux
                </h1>

                {/* Upload Card */}
                <div className="bg-white shadow-xl border border-gray-200 rounded-xl p-8 mb-8">
                    {/* Model Selection */}
                    <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <label className="block text-sm font-medium text-blue-700 mb-3 flex items-center gap-2">
                            <Cpu className="w-4 h-4" />
                            Sélection du Modèle IA
                        </label>
                        <div className="space-y-3">
                            {AVAILABLE_MODELS.map((model) => (
                                <div key={model.value} className="flex items-center">
                                    <input
                                        type="radio"
                                        id={`table-${model.value}`}
                                        name="table-model"
                                        value={model.value}
                                        checked={selectedModel === model.value}
                                        onChange={(e) => setSelectedModel(e.target.value)}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                    />
                                    <label htmlFor={`table-${model.value}`} className="ml-3 block text-sm font-medium text-gray-700">
                                        {model.label}
                                    </label>
                                </div>
                            ))}
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                            Modèle sélectionné: <span className="font-medium">
                                {AVAILABLE_MODELS.find(m => m.value === selectedModel)?.label}
                            </span>
                        </div>
                    </div>

                    <label
                        htmlFor="table-file-upload"
                        className={`block w-full text-center py-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${file ? 'border-green-400 bg-green-50' : 'border-blue-300 hover:border-blue-500 bg-gray-50'
                            }`}
                    >
                        {file ? (
                            <div className="text-green-700 font-semibold flex items-center justify-center gap-2">
                                <CheckCircle className="w-5 h-5" /> Fichier sélectionné: {file.name}
                            </div>
                        ) : (
                            <div className="text-blue-600 font-semibold">
                                <FileText className="w-6 h-6 mx-auto mb-2" />
                                Cliquez pour choisir un document contenant des tableaux (PDF, JPG, PNG)
                            </div>
                        )}
                        <input
                            id="table-file-upload"
                            type="file"
                            onChange={handleFileChange}
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png"
                        />
                    </label>

                    <button
                        onClick={handleExtractTable}
                        disabled={!file || loading}
                        className="w-full mt-6 flex items-center justify-center gap-3 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors shadow-md hover:shadow-lg text-lg"
                    >
                        {loading ? (
                            <>Extraction en cours...</>
                        ) : (
                            <>Lancer l'Extraction</>
                        )}
                    </button>
                </div>

                {/* Error Display */}
                {error && (
                    <div className="p-4 mb-6 bg-red-100 border-l-4 border-red-500 text-red-700 rounded-lg font-medium">
                        {error}
                    </div>
                )}

                {/* Results */}
                {tableResult && (
                    <div className="bg-white shadow-xl border border-gray-200 rounded-xl p-6">
                        {formatTableData(tableResult.tables)}
                    </div>
                )}

            </div>
        </div>
    );
}
